import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckIcon, Trash01Icon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSelect, FormSwitch } from '@/components/forms'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { usePartner, useUpdatePartner, useDeletePartner, usePartnerUsers } from '@/hooks/api/usePartners'
import type { PartnerType } from '@/types/partner'

// Bonnie default support user
const BONNIE_SUPPORT_USER = {
  id: 133,
  first_name: 'Team Bonnie',
  last_name: '(Bonnie Support)',
  email: 'support@bonnie.co',
}

// Validation schema
const partnerEditSchema = z.object({
  name: z.string().min(1, 'app.validation.name_required'),
  partner_type: z.enum(['reseller', 'distributor', 'referral']).nullable().optional(),
  hide_referral_link: z.boolean(),
  revenue_share_percentage: z
    .number()
    .min(0, 'app.validation.revenue_share_percentage_range')
    .max(100, 'app.validation.revenue_share_percentage_range')
    .nullable()
    .optional(),
  revenue_share_period_months: z
    .number()
    .min(0, 'app.validation.revenue_share_period_range')
    .max(60, 'app.validation.revenue_share_period_range')
    .nullable()
    .optional(),
  support_email_user_id: z.number().nullable().optional(),
})

type PartnerEditFormValues = z.infer<typeof partnerEditSchema>

export function PartnerEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const partnerId = parseInt(id || '0', 10)

  // Partner type options with translations
  const partnerTypeOptions = useMemo(() => [
    { value: 'reseller', label: t('resources.partners.partner_types.reseller') },
    { value: 'distributor', label: t('resources.partners.partner_types.distributor') },
    { value: 'referral', label: t('resources.partners.partner_types.referral') },
  ], [t])

  // Fetch partner data
  const { data: partner, isLoading: partnerLoading } = usePartner(partnerId)

  // Fetch partner users for support email selector
  const { data: partnerUsers = [], isLoading: usersLoading } = usePartnerUsers(partnerId)

  // Mutations
  const updatePartner = useUpdatePartner()
  const deletePartner = useDeletePartner()

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<PartnerEditFormValues>({
    resolver: zodResolver(partnerEditSchema),
    defaultValues: {
      name: '',
      partner_type: null,
      hide_referral_link: false,
      revenue_share_percentage: null,
      revenue_share_period_months: null,
      support_email_user_id: BONNIE_SUPPORT_USER.id,
    },
  })

  // Reset form when partner data loads
  useEffect(() => {
    if (partner) {
      reset({
        name: partner.name,
        partner_type: (partner.meta?.partner_type as PartnerType) || null,
        hide_referral_link: partner.meta?.hide_referral_link || false,
        revenue_share_percentage: partner.meta?.revenue_share_percentage ?? null,
        revenue_share_period_months: partner.meta?.revenue_share_period_months ?? null,
        support_email_user_id: partner.meta?.support_email_user_id ?? BONNIE_SUPPORT_USER.id,
      })
    }
  }, [partner, reset])

  // Support email user options
  const supportUserOptions = useMemo(() => {
    const options = [
      {
        value: BONNIE_SUPPORT_USER.id,
        label: `${BONNIE_SUPPORT_USER.first_name} ${BONNIE_SUPPORT_USER.last_name} - ${BONNIE_SUPPORT_USER.email} ${t('resources.partners.default_support')}`,
      },
    ]

    // Add partner users
    partnerUsers.forEach((user) => {
      if (user.id !== BONNIE_SUPPORT_USER.id) {
        options.push({
          value: user.id,
          label: `${user.first_name} ${user.last_name || ''} - ${user.email}`,
        })
      }
    })

    return options
  }, [partnerUsers, t])

  // Handle form submission
  const onSubmit = async (data: PartnerEditFormValues) => {
    try {
      await updatePartner.mutateAsync({
        id: partnerId,
        name: data.name,
        meta: {
          partner_type: data.partner_type || undefined,
          hide_referral_link: data.hide_referral_link,
          revenue_share_percentage: data.revenue_share_percentage ?? undefined,
          revenue_share_period_months: data.revenue_share_period_months ?? undefined,
          support_email_user_id: data.support_email_user_id ?? undefined,
        },
      })
      toast.success(t('resources.partners.update_success'))
      navigate('/partners')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.partners.update_error')
      toast.error(message)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    try {
      await deletePartner.mutateAsync(partnerId)
      toast.success(t('resources.partners.delete_success'))
      navigate('/partners')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.partners.delete_error')
      toast.error(message)
    }
  }

  if (partnerLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!partner) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">{t('resources.partners.not_found')}</p>
        <Button color="secondary" onPress={() => navigate('/partners')} className="mt-4">
          {t('resources.partners.back_to_partners')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('resources.partners.edit_title', { name: partner.name })}
        subtitle={t('resources.partners.edit_subtitle')}
      />

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="name"
              label={t('resources.partners.fields.name')}
              placeholder={t('resources.partners.placeholders.name')}
              isRequired
            />

            <FormSelect
              control={control}
              name="partner_type"
              label={t('resources.partners.fields.partner_type')}
              options={partnerTypeOptions}
              placeholder={t('resources.partners.placeholders.partner_type')}
            />
          </div>

          {/* Referral Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSwitch
              control={control}
              name="hide_referral_link"
              label={t('resources.partners.fields.hide_referral_link')}
            />
          </div>

          {/* Revenue Share */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <div className="space-y-4">
              <FormInput
                control={control}
                name="revenue_share_percentage"
                label={t('resources.partners.fields.revenue_share_percentage')}
                type="number"
                placeholder={t('resources.partners.placeholders.revenue_share_percentage')}
              />

              <FormInput
                control={control}
                name="revenue_share_period_months"
                label={t('resources.partners.fields.revenue_share_period_months')}
                type="number"
                placeholder={t('resources.partners.placeholders.revenue_share_period_months')}
              />
            </div>
          </div>

          {/* Support Email */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSelect
              control={control}
              name="support_email_user_id"
              label={t('resources.partners.fields.support_email_user_id')}
              options={supportUserOptions}
              description={t('resources.partners.descriptions.support_email_user_id')}
              isDisabled={usersLoading}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button
              color="primary-destructive"
              type="button"
              onPress={() => setShowDeleteDialog(true)}
              iconLeading={Trash01Icon}
            >
              {t('resources.partners.delete_title')}
            </Button>

            <div className="flex items-center gap-3">
              <Button color="secondary" type="button" onPress={() => navigate('/partners')}>
                {t('resources.generic.cancel')}
              </Button>
              <Button
                color="primary"
                type="submit"
                isDisabled={isSubmitting || !isDirty}
                iconLeading={CheckIcon}
              >
                {isSubmitting ? t('resources.generic.loading') : t('resources.generic.save')}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('resources.partners.delete_title')}
        description={t('resources.partners.delete_description', { name: partner.name })}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
        isLoading={deletePartner.isPending}
      />
    </div>
  )
}

export default PartnerEditPage
